/**
 * Every list in this app has one of these. An empty state that only says
 * "No data" wastes the one moment where the person is definitely looking at the
 * screen and definitely does not know what to do next — so `description` should
 * say what would put something here.
 */
import type { ComponentType, ReactNode } from 'react'
import { InboxIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface EmptyStateProps {
  /** A lucide icon component, e.g. `CreditCardIcon`. */
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
  /** Plain and specific: "No subscriptions yet". */
  title: string
  /** What to do next. One sentence. */
  description?: ReactNode
  /** Usually a single button. */
  action?: ReactNode
  /** `inline` sits inside a card or table; `page` fills an empty screen. */
  size?: 'inline' | 'page'
  className?: string
}

export function EmptyState({
  icon: Icon = InboxIcon,
  title,
  description,
  action,
  size = 'inline',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'border-hairline flex flex-col items-center justify-center rounded-lg border border-dashed text-center',
        size === 'page' ? 'gap-3 px-6 py-16' : 'gap-2 px-4 py-10',
        className,
      )}
    >
      <Icon className={cn('text-dim', size === 'page' ? 'size-8' : 'size-6')} aria-hidden />
      <h3 className={cn('font-display font-medium', size === 'page' ? 'text-lg' : 'text-sm')}>
        {title}
      </h3>
      {description && (
        <p className="text-dim max-w-sm text-sm text-balance">{description}</p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  )
}
